import { useEffect, useState } from "react";
import { Loader2, Activity, CheckCircle2, AlertOctagon, AlertTriangle, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { getMyRedemptions } from "@/lib/api";

/**
 * AP-5A.3 — Read-only "redemption portfolio" strip for `/account`.
 *
 * Fetches `/api/redemptions?limit=20` once and renders:
 *   - a compact counter pill row (pending / fulfilled / rejected /
 *     failed) computed client-side from the response,
 *   - an "active / needs attention" mini-list showing up to 3 rows
 *     in non-terminal states.
 *
 * Honest empty/loading/error states. No new backend dependency.
 * Strictly read-only — every row links into the existing
 * `/redeem/:arcReferenceId` status page; no actions are exposed
 * from this component.
 */
const ACTIVE_STATUSES = new Set([
  "submitted",
  "pending_partner_review",
  "failed_retryable",
]);

const COUNTER_BUCKETS = [
  {
    key: "pending",
    label: "Pending",
    icon: Clock,
    tone: "text-[#00F0FF] border-[#00F0FF]/40 bg-[#00F0FF]/5",
    match: (s) => ACTIVE_STATUSES.has(s),
  },
  {
    key: "fulfilled",
    label: "Fulfilled",
    icon: CheckCircle2,
    tone: "text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/5",
    match: (s) => s === "fulfilled",
  },
  {
    key: "rejected",
    label: "Rejected",
    icon: AlertOctagon,
    tone: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5",
    match: (s) => s === "rejected",
  },
  {
    key: "failed",
    label: "Failed",
    icon: AlertTriangle,
    tone: "text-[#FFB800] border-[#FFB800]/40 bg-[#FFB800]/5",
    match: (s) => s === "failed_terminal",
  },
];

function shortRef(ref) {
  if (!ref) return "";
  return ref.length > 18 ? `${ref.slice(0, 12)}…${ref.slice(-4)}` : ref;
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function RedemptionSummary({ linked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(linked);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!linked) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await getMyRedemptions(20);
        if (alive) setData(out);
      } catch (_e) {
        if (alive) setError("network");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [linked]);

  if (!linked) {
    return null;
  }

  if (loading) {
    return (
      <div
        data-testid="account-redemption-summary-loading"
        className="glass rounded-md p-5 flex items-center gap-2 text-white/60"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-mono text-xs">loading portfolio…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="account-redemption-summary-error"
        className="glass rounded-md p-5"
      >
        <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
          Portfolio
        </h3>
        <p className="mt-2 font-mono text-[11px] text-[#FF3B30]">
          Could not load your portfolio. Try again shortly.
        </p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const counts = COUNTER_BUCKETS.map((b) => ({
    ...b,
    count: items.filter((row) => b.match(row.status)).length,
  }));
  const activeRows = items
    .filter((row) => ACTIVE_STATUSES.has(row.status))
    .slice(0, 3);

  return (
    <div
      data-testid="account-redemption-summary"
      data-redemption-count={items.length}
      className="glass rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
          Portfolio
        </h3>
        <span
          data-testid="account-redemption-summary-window"
          className="font-mono text-[10px] text-white/40"
        >
          last 20 redemptions
        </span>
      </div>

      {/* ---- Counter pill row ---- */}
      <div
        data-testid="account-redemption-counters"
        className="grid grid-cols-2 md:grid-cols-4 gap-2"
      >
        {counts.map(({ key, label, icon: Icon, tone, count }) => (
          <div
            key={key}
            data-testid={`account-redemption-counter-${key}`}
            data-counter-value={count}
            className={`flex items-center gap-2 rounded border px-3 py-2 ${tone}`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="font-display text-lg leading-none">{count}</span>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* ---- Active / needs attention list ---- */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-3.5 w-3.5 text-[#00F0FF]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            Needs attention
          </span>
        </div>
        {activeRows.length === 0 ? (
          <p
            data-testid="account-redemption-active-empty"
            className="font-mono text-[11px] text-white/50"
          >
            No active or pending redemptions right now.
          </p>
        ) : (
          <ul
            data-testid="account-redemption-active-list"
            className="divide-y divide-white/5"
          >
            {activeRows.map((row) => (
              <li
                key={row.arc_reference_id}
                data-testid={`account-redemption-active-row-${row.arc_reference_id}`}
                className="py-2 flex items-center gap-3"
              >
                <Link
                  to={`/redeem/${encodeURIComponent(row.arc_reference_id)}`}
                  className="flex-1 min-w-0 font-mono text-xs text-white/85 hover:text-[#00F0FF] truncate transition-colors"
                  title={row.arc_reference_id}
                >
                  {shortRef(row.arc_reference_id)}
                </Link>
                <span className="font-mono text-[10px] text-white/40">
                  {formatTs(row.created_at)}
                </span>
                <span className="font-display text-xs text-[#39FF14]">
                  {row.rate_snapshot?.eur_amount ?? "—"}
                  <span className="ml-1 text-[9px] text-[#00F0FF]">EUR</span>
                </span>
                <span className="shrink-0 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[#00F0FF] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em]">
                  {row.status === "failed_retryable" ? "Retryable" : "Pending"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
